import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { MobiTitle } from '@/components/shared/MobiTitle';
import { ApiNamePaths } from '@/constants/rest-api-name-path';
import StorageKeys from '@/constants/storageKeys';
import type { VehicleDriverRes } from '@/features/vehicle-driver/types/vehicleDriver';
import { apiDelete } from '@/modules/hooks/use-delete';
import { apiGet } from '@/modules/hooks/use-query';
import { useServiceType } from '@/modules/hooks/useServiceType';
import * as paths from '@/routes/paths';
import { settingState } from '@/state/settingState';
import { ServiceType } from '@/types/serviceType';

const ChevronLeftIcon: React.FC = () => (
  <svg className="nav-icon nav-icon-back" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M14.5 5L7.5 12L14.5 19" />
  </svg>
);

export const UpdatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [setting] = useRecoilState(settingState);

  // ドライバID（cognitoのdriverId）
  const driverId = setting?.driverId || '';

  // 車両ID
  const vehicleId = sessionStorage.getItem(StorageKeys.VEHICLE_ID);
  // ドライバ管理会社ID
  const serviceType = useServiceType();

  // エラーダイアログ
  const [modalShow, setModalShow] = useState<boolean>(false);
  const [vehicleDriverUpdateError, setVehicleDriverUpdateError] = useState('');

  const [updateButtonHiddenFlg, setUpdateButtonHiddenFlg] = useState<boolean>(true);

  const [vehicleDriverData, setVehicleDriverData] = useState<VehicleDriverRes>();
  const [disabledFlg, setDisabledFlg] = useState<boolean>(false);

  useEffect(() => {
    // 定時定路線・リアルタイムオンデマンドの場合
    if (serviceType === ServiceType.FixedRouteSchedule || serviceType === ServiceType.RealtimeOnDemand) {
      setUpdateButtonHiddenFlg(true);

      // 車両IDがセッションストレージに存在しない場合（車両紐づけがされてない場合）は、車両選択画面へ遷移する
      if (!vehicleId) {
        navigate(paths.vehicleDriverSelect);
      }
    }
    // 事前予約の場合
    if (serviceType === ServiceType.PreBooking) {
      setUpdateButtonHiddenFlg(false);
    }
  }, [serviceType, navigate, vehicleId]);

  useEffect(() => {
    apiGet(ApiNamePaths.vehicleDriverApi.apiName, ApiNamePaths.vehicleDriverApi.apiPath, {
      queryParams: {
        driverId,
      },
    })
      .then((res) => {
        const data = res as VehicleDriverRes;
        if (data && data?.vehicleDriverList.length > 0) {
          setVehicleDriverData(data);
          if (data?.vehicleDriverList[0]) {
            // セッションストレージに車両IDを保存（既に保存されてる場合は上書き保存）
            sessionStorage.setItem(StorageKeys.VEHICLE_ID, String(data?.vehicleDriverList[0]?.vehicleId));
          }
        }
        // ドライバーに車両が紐づけてない場合
        else {
          // セッションストレージから車両IDを削除する
          sessionStorage.removeItem(StorageKeys.VEHICLE_ID);
          // 車両選択画面へ遷移する
          navigate(paths.vehicleDriverSelect);
        }
        setModalShow(false);
      })
      .catch((error) => {
        setVehicleDriverUpdateError(error instanceof Error ? error?.message : '');
        setModalShow(true);
      });
  }, [driverId, navigate]);

  const onHandleClick = async () => {
    setDisabledFlg(true);
    apiDelete(ApiNamePaths.vehicleDriverApi.apiName, ApiNamePaths.vehicleDriverApi.apiPath, {
      queryParams: { driverId },
    })
      .then(() => {
        setModalShow(false);
        // セッションストレージから車両IDを削除する
        sessionStorage.removeItem(StorageKeys.VEHICLE_ID);
        // 正常終了後、車両選択画面へ遷移する
        navigate(paths.vehicleDriverSelect);
      })
      .catch((error) => {
        setVehicleDriverUpdateError((error as Error)?.message);
        setModalShow(true);
      })
      .finally(() => {
        setDisabledFlg(false);
      });
  };

  return (
    <div className="page-root">
      <MobiTitle>{t('vehicleDriver.pageTitle')}</MobiTitle>

      <div className="notification-top-bar">
        <button
          className="notification-icon-button"
          type="button"
          onClick={() => {
            navigate(paths.menu);
          }}
          aria-label={t('notification.back')}
        >
          <ChevronLeftIcon />
        </button>
        <h1 className="notification-header-text">{t('vehicleDriver.pageTitle')}</h1>
      </div>
      <div className="screen-shell">
        <div className="content-stack">
          <h1 className="screen-title">{t('vehicleDriver.updateTitle')}</h1>

          <div className="vehicle-panel">
            <p className="vehicle-text">
              {vehicleDriverData?.vehicleDriverList[0]?.vehicleRegionName}
              {` `}
              {vehicleDriverData?.vehicleDriverList[0]?.numberPlateClassCode}
              {` `}
              {vehicleDriverData?.vehicleDriverList[0]?.numberPlateHiraganaEtc}
              {` `}
              {(vehicleDriverData?.vehicleDriverList[0]?.numberPlateNumber ?? '').replace(/(\d{2})(\d{2})/, '$1-$2')}
            </p>
          </div>

          {updateButtonHiddenFlg ? (
            <button
              className="action-button"
              type="button"
              disabled={disabledFlg}
              onClick={() => {
                void onHandleClick();
              }}
            >
              {t('vehicleDriver.updateButton')}
            </button>
          ) : null}
        </div>
      </div>

      {modalShow ? (
        <div className="dialog-overlay" role="presentation">
          <div className="dialog-panel" role="dialog" aria-modal="true">
            <p className="dialog-message">{vehicleDriverUpdateError}</p>
            <div className="dialog-actions">
              <button
                className="dialog-button"
                type="button"
                onClick={() => {
                  setModalShow(false);
                }}
              >
                {t('vehicleDriver.close')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
